import type { EditorView, ViewMutationRecord } from '@tiptap/pm/view';
import type { TableCellOptions, TableHeaderOptions, TableOptions, TableRowOptions } from '@tiptap/extension-table';
// Vendor builds on the plain @tiptap/extension-table nodes. ConvertKit's carry the
// DOCX attributes the exporter reads, and this is what extension-pages-tablekit did
// internally before we moved off it. Borders are no longer among those attributes —
// since convert-kit 0.9.0 the exporter writes every edge as `none`, so export-utils
// puts them back via `tableOverrides`.
import {
  ConvertKitTable as Table,
  ConvertKitTableCell as TableCell,
  ConvertKitTableHeader as TableHeader,
  ConvertKitTableRow as TableRow,
} from '@tiptap-pro/extension-convert-kit';
import type { Node } from '@tiptap/pm/model';
import type { CommandProps } from '@tiptap/core';
import { Extension } from '@tiptap/core';
import {
  addColumnAfter,
  addColumnBefore,
  cellAround,
  columnResizing,
  columnResizingPluginKey,
  mergeCells,
  splitCell,
  tableEditing,
  TableView,
} from '@tiptap/pm/tables';
import type { Command as PMCommand } from '@tiptap/pm/state';
import { EditorState, Selection, TextSelection } from '@tiptap/pm/state';

import {
  EMPTY_CELL_WIDTH,
  RESIZE_MIN_WIDTH,
} from 'containers/Tiptap/components/tiptap-node/table-node/lib/tiptap-table-utils';
import { tableContainerStyles } from 'containers/Tiptap/components/tiptap-node/table-node/lib/table-container-styles';
import {
  isPagesIntegrationActive,
  TablePagesIntegration,
} from 'containers/Tiptap/components/tiptap-node/table-node/extensions/table-pages-integration';

/**
 * prosemirror-tables' addColumn, mergeCells and splitCell map their positions
 * through the transaction's full mapping, which assumes the transaction is
 * empty when the command starts. In a tiptap chain all commands share one
 * transaction, so steps appended by earlier commands are applied to the
 * positions a second time and corrupt the table, e.g.
 * chain().addRowAfter().addColumnAfter() scatters cells and splits their
 * content. Running the command on a snapshot of the transaction's current
 * point restores that precondition; the resulting steps replay onto the
 * shared transaction unchanged because both docs are identical.
 */
function chainSafe(command: PMCommand) {
  return ({ state, tr, dispatch }: CommandProps) => {
    if (!dispatch) {
      return command(state, undefined);
    }

    const snapshot = EditorState.create({
      doc: tr.doc,
      selection: tr.selection,
    });

    return command(snapshot, (result) => {
      for (const step of result.steps) {
        tr.step(step);
      }

      if (result.selectionSet) {
        tr.setSelection(Selection.fromJSON(tr.doc, result.selection.toJSON()));
      }
    });
  };
}

export const TableNode = Table.extend<TableOptions>({
  addCommands() {
    return {
      ...this.parent?.(),
      addColumnBefore: () => chainSafe(addColumnBefore),
      addColumnAfter: () => chainSafe(addColumnAfter),
      mergeCells: () => chainSafe(mergeCells),
      splitCell: () => chainSafe(splitCell),
      mergeOrSplit: () => chainSafe((state, dispatch) => mergeCells(state, dispatch) || splitCell(state, dispatch)),
    };
  },

  addProseMirrorPlugins() {
    const isResizable = this.options.resizable && this.editor.isEditable;

    const defaultCellMinWidth =
      this.options.cellMinWidth < EMPTY_CELL_WIDTH ? EMPTY_CELL_WIDTH : this.options.cellMinWidth;

    return [
      ...(isResizable
        ? [
            columnResizing({
              handleWidth: this.options.handleWidth,
              cellMinWidth: RESIZE_MIN_WIDTH,
              defaultCellMinWidth,
              View: null,
              lastColumnResizable: this.options.lastColumnResizable,
            }),
          ]
        : []),
      tableEditing({
        allowTableNodeSelection: this.options.allowTableNodeSelection,
      }),
    ];
  },

  addNodeView() {
    return ({ node, HTMLAttributes, editor, view }) => {
      /**
       * Whether the Pages extension is registered on this editor. When it
       * is, the view attaches the optional {@link TablePagesIntegration},
       * which adapts the table to Pages' paginated rendering model
       * (rows as grid blocks flowing around floated page breakers).
       * Without Pages nothing pagination-related is instantiated and the
       * view behaves exactly as before.
       */
      const pagesActive = isPagesIntegrationActive(editor);

      class TiptapTableView extends TableView {
        private readonly blockContainer: HTMLDivElement;
        private readonly innerTableContainer: HTMLDivElement;
        private readonly widgetsContainer: HTMLDivElement;
        private readonly overlayContainer: HTMLDivElement;

        // Vendor's `declare node` / `declare minCellWidth` dropped — our Jest Babel
        // runs class-properties before preset-typescript and throws on declare fields.
        private readonly containerAttributes: Record<string, string>;
        private readonly editorView: EditorView;
        private readonly pagesIntegration?: TablePagesIntegration;
        private appliedContainerStyles: string[] = [];

        constructor(
          node: Node,
          minCellWidth: number,
          containerAttributes: Record<string, string>,
          editorView: EditorView
        ) {
          super(node, minCellWidth);

          this.containerAttributes = containerAttributes ?? {};
          this.editorView = editorView;

          this.blockContainer = this.createBlockContainer();
          this.innerTableContainer = this.createInnerTableContainer();
          this.widgetsContainer = this.createWidgetsContainer();
          this.overlayContainer = this.createOverlayContainer();

          this.setupDOMStructure();
          this.applyContainerStyles(node);

          if (pagesActive) {
            this.pagesIntegration = new TablePagesIntegration(
              {
                view: editorView,
                blockContainer: this.blockContainer,
                colgroup: this.colgroup as HTMLElement,
                contentDOM: this.contentDOM as HTMLElement,
              },
              node
            );
          }
        }

        private createBlockContainer(): HTMLDivElement {
          const container = document.createElement('div');
          container.setAttribute('data-content-type', 'table');

          this.applyContainerAttributes(container);
          return container;
        }

        private createInnerTableContainer(): HTMLDivElement {
          const container = document.createElement('div');
          container.className = 'table-container';
          return container;
        }

        private createWidgetsContainer(): HTMLDivElement {
          const container = document.createElement('div');
          container.className = 'table-controls';
          container.style.position = 'relative';
          return container;
        }

        private createOverlayContainer(): HTMLDivElement {
          const container = document.createElement('div');
          container.className = 'table-selection-overlay-container';
          return container;
        }

        private applyContainerAttributes(element: HTMLDivElement): void {
          Object.entries(this.containerAttributes).forEach(([key, value]) => {
            if (key !== 'class' && key !== 'style') {
              element.setAttribute(key, value);
            }
          });
        }

        private applyContainerStyles(node: Node): void {
          const { style } = this.blockContainer;
          this.appliedContainerStyles.forEach((property) => style.removeProperty(property));

          const styles = tableContainerStyles(node.attrs);
          Object.entries(styles).forEach(([property, value]) => style.setProperty(property, value));
          this.appliedContainerStyles = Object.keys(styles);
        }

        private setupDOMStructure(): void {
          const originalTable = this.dom;
          const tableElement = originalTable.firstChild!;

          // Move table into inner container
          this.innerTableContainer.appendChild(tableElement);

          // Build the hierarchy: blockContainer > originalTable > innerContainer + widgetsContainer
          originalTable.appendChild(this.innerTableContainer);
          originalTable.appendChild(this.widgetsContainer);
          originalTable.appendChild(this.overlayContainer);

          this.blockContainer.appendChild(originalTable);

          this.dom = this.blockContainer;
        }

        update(node: Node): boolean {
          if (!this.pagesIntegration) {
            const updated = super.update(node);
            if (updated) {
              this.applyContainerStyles(node);
            }
            return updated;
          }

          if (node.type !== this.node.type) {
            return false;
          }

          // During a column drag prosemirror-tables owns the `<col>` widths
          // directly (every mousemove writes the dragged width), while the
          // PM `colwidth` attr still holds the pre-drag value. Under Pages,
          // unrelated transactions (page-count recalculations) arrive
          // mid-drag; letting the parent rebuild `<col>` widths from that
          // stale attr would snap the dragged column back. `dragging` is
          // tri-state: only its object form means an actual drag.
          const resizeState = columnResizingPluginKey.getState(this.editorView.state);
          const isDragging = !!resizeState?.dragging;
          if (isDragging) {
            this.node = node;
          } else {
            super.update(node);
          }

          this.applyContainerStyles(node);
          this.pagesIntegration.onNodeUpdated(node);

          return true;
        }

        ignoreMutation(mutation: ViewMutationRecord): boolean {
          const target = mutation.target as HTMLElement;
          const isInsideTable = target.closest('.table-container');

          return !isInsideTable || super.ignoreMutation(mutation);
        }

        destroy(): void {
          this.pagesIntegration?.destroy();
        }
      }

      const cellMinWidth = this.options.cellMinWidth < EMPTY_CELL_WIDTH ? EMPTY_CELL_WIDTH : this.options.cellMinWidth;
      return new TiptapTableView(node, cellMinWidth, HTMLAttributes, view);
    };
  },
});

const TableCellNode = TableCell.extend<TableCellOptions>({
  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      'Mod-a': () => {
        const { state, view } = this.editor;
        const { selection, doc } = state;

        const $anchor = selection.$anchor;
        const cellPos = cellAround($anchor);
        if (!cellPos) {
          return false;
        }

        const cellNode = doc.nodeAt(cellPos.pos);
        if (!cellNode || !cellNode.textContent) {
          return false;
        }

        const from = cellPos.pos + 1;
        const to = cellPos.pos + cellNode.nodeSize - 1;

        if (from >= to) {
          return true;
        }

        const $from = doc.resolve(from);
        const $to = doc.resolve(to);

        const nextSel = TextSelection.between($from, $to, 1);
        if (!nextSel) {
          return true;
        }

        if (state.selection.eq(nextSel)) {
          return true;
        }

        view.dispatch(state.tr.setSelection(nextSel));
        return true;
      },
    };
  },
});

export interface TableNodeOptions {
  /**
   * If set to false, the table extension will not be registered
   * @example table: false
   */
  table: Partial<TableOptions> | false;
  /**
   * If set to false, the table extension will not be registered
   * @example tableCell: false
   */
  tableCell: Partial<TableCellOptions> | false;
  /**
   * If set to false, the table extension will not be registered
   * @example tableHeader: false
   */
  tableHeader: Partial<TableHeaderOptions> | false;
  /**
   * If set to false, the table extension will not be registered
   * @example tableRow: false
   */
  tableRow: Partial<TableRowOptions> | false;
}

/**
 * The table kit is a collection of table editor extensions.
 *
 * It’s a good starting point for building your own table in Tiptap.
 */
export const TableKit = Extension.create<TableNodeOptions>({
  name: 'tableKit',

  addExtensions() {
    const extensions = [];

    if (this.options.table !== false) {
      extensions.push(TableNode.configure(this.options.table));
    }

    if (this.options.tableCell !== false) {
      extensions.push(TableCellNode.configure(this.options.tableCell));
    }

    if (this.options.tableHeader !== false) {
      extensions.push(TableHeader.configure(this.options.tableHeader));
    }

    if (this.options.tableRow !== false) {
      extensions.push(TableRow.configure(this.options.tableRow));
    }

    return extensions;
  },
});
